import { iSettings } from "@/lib/types";
import { Button } from "../ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { FormCheckbox } from "../ui/formcheckbox";
import { words } from "@/lib/words";
import { Label, Pie, PieChart } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../ui/chart";
import { chartConfig } from "./chart";
import colors from "tailwindcss/colors";

interface iHome {
  onQuizStart: () => void;
  settings: iSettings;
  setSettings: (newSettings: Partial<iSettings>) => void;
  summary: {
    weak?: number;
    medium?: number;
    strong?: number;
  };
}

export const Home = ({
  onQuizStart,
  settings,
  setSettings,
  summary,
}: iHome) => {
  const calculateNewWords = () => {
    const strong = summary?.strong || 0;
    const medium = summary?.medium || 0;
    const weak = summary?.weak || 0;

    return Object.keys(words).length - (strong + medium + weak);
  };

  const strongWordsCount = summary?.strong || 0;
  const mediumWordsCount = summary?.medium || 0;
  const weakWordsCount = summary?.weak || 0;
  const newWordsCount = calculateNewWords();

  const totalOccurrences = Object.values(words).reduce(
    (acc, { occurrence }) => acc + occurrence,
    0,
  );

  const totalWordsInQuran = 77797;

  const chartData = [
    {
      type: "Covered",
      count: totalOccurrences,
      fill: colors.green[700],
    },
    {
      type: "Not Covered",
      count: totalWordsInQuran - totalOccurrences,

      fill: colors.red[700],
    },
  ];

  return (
    <>
      <Card className="bg-card-adaptive mt-10">
        <CardHeader>
          <CardTitle className="text-title-adaptive">Statistics</CardTitle>
        </CardHeader>
        <CardContent className="text-body-adaptive">
          <ChartContainer
            config={chartConfig}
            className="mx-auto aspect-square max-h-[250px]"
          >
            <PieChart>
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel />}
              />
              <Pie
                data={chartData}
                dataKey="count"
                nameKey="type"
                innerRadius={70}
                strokeWidth={12}
              >
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                      return (
                        <text
                          x={viewBox.cx}
                          y={viewBox.cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          <tspan
                            x={viewBox.cx}
                            y={viewBox.cy}
                            className="fill-foreground text-3xl font-bold dark:fill-gray-100"
                          >
                            {totalWordsInQuran.toLocaleString()}
                          </tspan>
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 24}
                            className="fill-muted-foreground dark:fill-gray-400"
                          >
                            words in Quran
                          </tspan>
                        </text>
                      );
                    }
                  }}
                />
              </Pie>
            </PieChart>
          </ChartContainer>
          <div className="text-body-adaptive mt-5 text-center">
            <span className="text-xl font-bold text-gray-100">
              {Math.round((totalOccurrences / totalWordsInQuran) * 100)}%
            </span>
            &nbsp;
            <span>coverage</span>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-card-adaptive mt-10">
        <CardHeader>
          <CardTitle className="text-body-adaptive">Quiz Settings</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <FormCheckbox
            id="strong-toggle"
            label={`Show strong words (${strongWordsCount})`}
            checkboxProps={{
              disabled: strongWordsCount === 0,
              checked: settings?.strong,
              onCheckedChange: () => {
                setSettings({
                  strong: !settings?.strong,
                });
              },
            }}
          />
          <FormCheckbox
            id="medium-toggle"
            label={`Show medium words (${mediumWordsCount})`}
            checkboxProps={{
              disabled: mediumWordsCount === 0,
              checked: settings?.medium,
              onCheckedChange: () => {
                setSettings({
                  medium: !settings?.medium,
                });
              },
            }}
          />
          <FormCheckbox
            id="weak-toggle"
            label={`Show weak words (${weakWordsCount})`}
            checkboxProps={{
              disabled: weakWordsCount === 0,
              checked: settings?.weak,
              onCheckedChange: () => {
                setSettings({
                  weak: !settings?.weak,
                });
              },
            }}
          />
          <FormCheckbox
            id="new-toggle"
            label={`Show new words (${newWordsCount})`}
            checkboxProps={{
              disabled: newWordsCount === 0,
              checked: settings?.new,
              onCheckedChange: () => {
                setSettings({ new: !settings?.new });
              },
            }}
          />

          <hr className="my-5" />

          <Button onClick={onQuizStart} className="mt-5">
            Start Quiz
          </Button>
        </CardContent>
      </Card>
    </>
  );
};
